import { PageHeader } from '@ksyos/design-system';
import { useParams, useSearchParams } from '@ksyos/router';
import { useSelector } from 'react-redux';
import type { IReduxState } from '@frontend/configureStore';
import { ManagerAuthorization, PractitionerRole } from '@common/enums';
import { getSelectedLanguage } from '@frontend/translate';
import { assertHasAuthorization, hasOneAuthorization } from '@frontend/utils';
import { hasPractitionerRole } from '@frontend/utils/hasPractitionerRole';
import { useFrontendSettings } from '@frontend/utils/hooks/useFrontendSettings';
import { EditForm } from '../OrganizationView';
import { OrganizationFinancialSettings } from './OrganizationFinancialSettings';
import { OrganizationGeneralSettings } from './OrganizationGeneralSettings';
import { OrganizationKsyosAccount } from './OrganizationKsyosAccount';
import { OrganizationPayoutManagers } from './OrganizationPayoutManagers';

export function OrganizationDetails() {
    const [searchParams] = useSearchParams();
    const { frontendSettings } = useFrontendSettings();
    const translate = getSelectedLanguage();
    const { organizationId } = useParams() as { organizationId: string };
    const editForm = searchParams.getOptional('editForm') as EditForm;
    const focusField = searchParams.getOptional('focusField');

    const isOrganizationPayoutManager = hasPractitionerRole(
        useSelector((state: IReduxState) => state.authentication).employmentPractitionerRoles,
        PractitionerRole.organizationPayoutManager
    );

    if (!isOrganizationPayoutManager) {
        assertHasAuthorization(ManagerAuthorization.organizationRead);
    }

    if (!organizationId) {
        return null;
    }

    return (
        <>
            <PageHeader title={translate.organization.details} />

            <OrganizationGeneralSettings
                editMode={editForm === EditForm.generalSettings}
                focusField={focusField}
                organizationId={organizationId}
            />

            {hasOneAuthorization(ManagerAuthorization.organizationRead) && (
                <OrganizationKsyosAccount organizationId={organizationId} />
            )}

            <OrganizationFinancialSettings
                isReadOnly={
                    !hasOneAuthorization([
                        ManagerAuthorization.organizationAddEdit,
                        ManagerAuthorization.organizationRelationNumberEdit,
                    ])
                }
                organizationId={organizationId}
            />

            {frontendSettings.organizationPayoutManagerEnabled &&
                hasOneAuthorization([
                    ManagerAuthorization.employmentSettingsRead,
                    ManagerAuthorization.employmentSettingsEdit,
                ]) && <OrganizationPayoutManagers organizationId={organizationId} />}
        </>
    );
}
